import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { IAttempt } from "../../types/images";
import { RootState } from "../../redux";
import { QueueEndpoints, Subdomains } from "../../constants";
import { cleanUnderReviewAPI, getCaseForReviewAPI, getClaimsTypeAPI, submitDecisionAPI } from "./QueueAPI";
import { logoutAdminAction } from "../Dashboard/DashboardSlice";

export interface QueueState {
  attempts: IAttempt[] | null;
  claimsTypes: string[] | [];
  claimType: string | null;
  queueType: null | string;
  auditDecision: string | null;
  auditReason: string;
  auditComment: string | null;
}
const queueState: QueueState = {
  attempts: null,
  claimsTypes: [],
  claimType: null,
  queueType: null,
  auditDecision: null,
  auditComment: null,
  auditReason: "",
};

export const getClaimsTypeAction = createAsyncThunk(
  `${Subdomains.admins}${QueueEndpoints.getClaimsTypes}`,
  async (queueType: string) => {
    const response = await getClaimsTypeAPI(queueType);
    return response.data;
  }
);

export const getCaseForReviewAction = createAsyncThunk(
  `${Subdomains.admins}${QueueEndpoints.getAttemptsForReview}`,
  async (args: { queueType: string; claimType: string; userInfoId: number | null; regNum: number | null }) => {
    const response = await getCaseForReviewAPI(args);

    return response.data;
  }
);

export const cleanUnderReviewAction = createAsyncThunk(
  `${Subdomains.admins}${QueueEndpoints.cleanUnderReview}`,
  async () => {
    const response = await cleanUnderReviewAPI();
    return response.data;
  }
);

export const submitDecisionAction = createAsyncThunk(
  `${Subdomains.admins}${QueueEndpoints.decision}`,
  async (args: {
    queueType: string;
    attempts: IAttempt[];
    decision: string;
    reason: string;
    varification: boolean;
    comment: string | null;
  }) => {
    const response = await submitDecisionAPI(args);
    return response.data;
  }
);

export const queueSlice = createSlice({
  name: "queue",
  initialState: queueState,
  reducers: {
    clearAttepts: (state) => {
      state.attempts = null;
    },
    setQueueTypeAction: (state, { payload }) => {
      state.queueType = payload;
    },
    setClaimsTypeAction: (state, { payload }) => {
      state.claimType = payload;
    },
    setAttempts: (state, { payload }) => {
      state.attempts = payload;
    },
    setAuditDecision: (state, { payload }) => {
      state.auditDecision = payload;
    },
    setAuditReason: (state, { payload }) => {
      state.auditReason = payload;
    },
    setAuditComment: (state, { payload }) => {
      state.auditComment = payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(getClaimsTypeAction.fulfilled, (state, { payload }) => {
      state.claimsTypes = payload.claimsTypes;
    });

    builder.addCase(cleanUnderReviewAction.fulfilled, (state) => {
      state.queueType = null;
      state.claimType = null;
      state.attempts = null;
      state.auditComment = null;
      state.auditDecision = null;
      state.auditReason = "";
    });

    builder.addMatcher(
      isAnyOf(logoutAdminAction.fulfilled, logoutAdminAction.rejected, cleanUnderReviewAction.fulfilled),
      (state) => {
        state.queueType = null;
        state.claimType = null;
        state.attempts = null;
        state.auditComment = null;
        state.auditDecision = null;
        state.auditReason = "";
      }
    );

    builder.addMatcher(isAnyOf(getCaseForReviewAction.fulfilled), (state, { payload }) => {
      state.attempts = payload.attempts;
    });
  },
});

export const {
  setQueueTypeAction,
  setClaimsTypeAction,
  clearAttepts,
  setAuditDecision,
  setAuditReason,
  setAuditComment,
  setAttempts,
} = queueSlice.actions;

export const selectAttempts = (state: RootState) => state.queue.attempts;
export const selectClaimsTypes = (state: RootState) => state.queue.claimsTypes;
export const selectClaimType = (state: RootState) => state.queue.claimType;
export const selectQueueType = (state: RootState) => state.queue.queueType;
export const selectAuditDecision = (state: RootState) => state.queue.auditDecision;
export const selectAuditComment = (state: RootState) => state.queue.auditComment;
export const selectAuditReason = (state: RootState) => state.queue.auditReason;

export default queueSlice.reducer;
